import {
  AppData,
  Athlete,
  Attempt,
  Judge,
  Route,
  AthleteScore,
  RouteResult,
} from "@shared/types";

// Official route scoring configuration
const OFFICIAL_SCORING = [
  { via: 1, flash: 120, tentativa2mais: 102 },
  { via: 2, flash: 131, tentativa2mais: 111 },
  { via: 3, flash: 142, tentativa2mais: 121 },
  { via: 4, flash: 153, tentativa2mais: 130 },
  { via: 5, flash: 164, tentativa2mais: 139 },
  { via: 6, flash: 175, tentativa2mais: 149 },
  { via: 7, flash: 186, tentativa2mais: 158 },
  { via: 8, flash: 197, tentativa2mais: 167 },
  { via: 9, flash: 251, tentativa2mais: 213 },
  { via: 10, flash: 273, tentativa2mais: 232 },
  { via: 11, flash: 295, tentativa2mais: 251 },
  { via: 12, flash: 317, tentativa2mais: 269 },
  { via: 13, flash: 339, tentativa2mais: 288 },
  { via: 14, flash: 361, tentativa2mais: 307 },
  { via: 15, flash: 383, tentativa2mais: 326 },
  { via: 16, flash: 405, tentativa2mais: 344 },
  { via: 17, flash: 424, tentativa2mais: 445 },
  { via: 18, flash: 568, tentativa2mais: 483 },
  { via: 19, flash: 612, tentativa2mais: 520 },
  { via: 20, flash: 656, tentativa2mais: 558 },
  { via: 21, flash: 700, tentativa2mais: 595 },
  { via: 22, flash: 744, tentativa2mais: 632 },
  { via: 23, flash: 788, tentativa2mais: 670 },
  { via: 24, flash: 826, tentativa2mais: 702 },
  { via: 25, flash: 1092, tentativa2mais: 928 },
  { via: 26, flash: 1180, tentativa2mais: 1003 },
  { via: 27, flash: 1268, tentativa2mais: 1078 },
  { via: 28, flash: 1356, tentativa2mais: 1153 },
  { via: 29, flash: 1444, tentativa2mais: 1227 },
  { via: 30, flash: 1532, tentativa2mais: 1302 },
  { via: 31, flash: 1614, tentativa2mais: 1372 },
  { via: 32, flash: 1696, tentativa2mais: 1442 },
  { via: 33, flash: 1740, tentativa2mais: 1479 },
  { via: 34, flash: 2272, tentativa2mais: 1931 },
  { via: 35, flash: 2448, tentativa2mais: 2081 },
  { via: 36, flash: 2624, tentativa2mais: 2230 },
  { via: 37, flash: 2800, tentativa2mais: 2380 },
  { via: 38, flash: 2986, tentativa2mais: 2530 },
  { via: 39, flash: 3146, tentativa2mais: 2674 },
  { via: 40, flash: 3310, tentativa2mais: 2814 },
];

// Route configuration with official points
export const ROUTES: Route[] = OFFICIAL_SCORING.map((route) => ({
  number: route.via,
  flashPoints: route.flash,
  secondPlusPoints: route.tentativa2mais,
}));

// Initialize default judges
const DEFAULT_JUDGES: Judge[] = [
  { id: "1", password: "juiz1", name: "Juiz 1" },
  { id: "2", password: "juiz2", name: "Juiz 2" },
  { id: "3", password: "juiz3", name: "Juiz 3" },
];

class DataStore {
  private data: AppData;
  private idCounter: number = 0;

  constructor() {
    this.data = this.loadData();
    // Initialize counter based on existing data to avoid conflicts
    this.idCounter = Math.max(
      ...this.data.athletes.map((a) => parseInt(a.id) || 0),
      ...this.data.attempts.map((a) => parseInt(a.id) || 0),
      Date.now(),
    );
    // Save migrated data to ensure clean state
    this.saveData();
  }

  private generateUniqueId(): string {
    this.idCounter++;
    return this.idCounter.toString();
  }

  private loadData(): AppData {
    const stored = localStorage.getItem("boulder-festival-data");
    if (stored) {
      const data = JSON.parse(stored);
      return this.migrateData(data);
    }
    return {
      athletes: [],
      attempts: [],
      judges: DEFAULT_JUDGES,
      csvImported: false,
    };
  }

  private migrateData(data: AppData): AppData {
    // Fix duplicate IDs in existing data
    const seenIds = new Set<string>();
    let tempCounter = Date.now();

    // Fix athlete IDs
    data.athletes = data.athletes.map((athlete) => {
      if (seenIds.has(athlete.id)) {
        // Duplicate ID found, generate new one
        tempCounter++;
        return { ...athlete, id: tempCounter.toString() };
      }
      seenIds.add(athlete.id);
      return athlete;
    });

    // Fix attempt IDs
    data.attempts = data.attempts.map((attempt) => {
      if (seenIds.has(attempt.id)) {
        // Duplicate ID found, generate new one
        tempCounter++;
        return { ...attempt, id: tempCounter.toString() };
      }
      seenIds.add(attempt.id);
      return attempt;
    });

    return data;
  }

  private saveData(): void {
    localStorage.setItem("boulder-festival-data", JSON.stringify(this.data));
  }

  // Athletes
  getAthletes(): Athlete[] {
    return this.data.athletes;
  }

  addAthlete(athlete: Omit<Athlete, "id" | "createdAt">): Athlete {
    const newAthlete: Athlete = {
      ...athlete,
      id: this.generateUniqueId(),
      createdAt: new Date().toISOString(),
    };
    this.data.athletes.push(newAthlete);
    this.saveData();
    return newAthlete;
  }

  importAthletes(athletes: Omit<Athlete, "id" | "createdAt">[]): {
    imported: number;
    duplicates: number;
    errors: number;
  } {
    const existingNames = new Set(
      this.data.athletes.map((a) => a.name.toLowerCase()),
    );
    let importedCount = 0;
    let duplicatesCount = 0;

    athletes.forEach((athlete) => {
      if (!existingNames.has(athlete.name.toLowerCase())) {
        this.addAthlete(athlete);
        existingNames.add(athlete.name.toLowerCase()); // Add to set to avoid duplicates within the same import
        importedCount++;
      } else {
        duplicatesCount++;
      }
    });

    this.data.csvImported = true;
    this.saveData();

    return {
      imported: importedCount,
      duplicates: duplicatesCount,
      errors: 0, // Will be calculated in the frontend
    };
  }

  removeAthlete(athleteId: string): boolean {
    const initialLength = this.data.athletes.length;
    this.data.athletes = this.data.athletes.filter(
      (athlete) => athlete.id !== athleteId,
    );

    // Also remove all attempts for this athlete
    this.data.attempts = this.data.attempts.filter(
      (attempt) => attempt.athleteId !== athleteId,
    );

    if (this.data.athletes.length < initialLength) {
      this.saveData();
      return true;
    }
    return false;
  }

  isCsvImported(): boolean {
    return this.data.csvImported;
  }

  // Attempts
  getAttempts(): Attempt[] {
    return this.data.attempts;
  }

  getAthleteAttempts(athleteId: string): Attempt[] {
    return this.data.attempts.filter((a) => a.athleteId === athleteId);
  }

  getRouteAttempts(athleteId: string, routeNumber: number): Attempt[] {
    return this.data.attempts
      .filter((a) => a.athleteId === athleteId && a.routeNumber === routeNumber)
      .sort((a, b) => a.attemptNumber - b.attemptNumber);
  }

  addAttempt(
    athleteId: string,
    routeNumber: number,
    completed: boolean,
  ): Attempt {
    const existingAttempts = this.getRouteAttempts(athleteId, routeNumber);
    const attemptNumber = existingAttempts.length + 1;

    // Check if already completed this route
    const hasCompleted = existingAttempts.some((a) => a.result === "completed");

    let points = 0;
    let pointType: "flash" | "second+" | "none" = "none";

    if (completed && !hasCompleted) {
      const route = ROUTES.find((r) => r.number === routeNumber)!;
      if (attemptNumber === 1) {
        points = route.flashPoints;
        pointType = "flash";
      } else {
        points = route.secondPlusPoints;
        pointType = "second+";
      }
    }

    const attempt: Attempt = {
      id: this.generateUniqueId(),
      athleteId,
      routeNumber,
      timestamp: new Date().toISOString(),
      result: completed ? "completed" : "failed",
      attemptNumber,
      points,
      pointType,
    };

    this.data.attempts.push(attempt);
    this.saveData();
    return attempt;
  }

  removeLastAttempt(athleteId: string, routeNumber: number): boolean {
    console.log(
      `🗑️ Removing last attempt for athlete ${athleteId}, route ${routeNumber}`,
    );

    const attempts = this.getRouteAttempts(athleteId, routeNumber);

    if (attempts.length === 0) {
      console.log("❌ No attempts found to remove");
      return false;
    }

    // Get the latest attempt (highest attempt_number)
    const lastAttempt = attempts.sort(
      (a, b) => b.attemptNumber - a.attemptNumber,
    )[0];

    console.log(
      `🗑️ Removing attempt: ${lastAttempt.id} (attempt #${lastAttempt.attemptNumber})`,
    );

    // Remove from the data array
    const attemptIndex = this.data.attempts.findIndex(
      (a) => a.id === lastAttempt.id,
    );
    if (attemptIndex !== -1) {
      this.data.attempts.splice(attemptIndex, 1);
      this.saveData();
      console.log(`✅ Successfully removed attempt ${lastAttempt.id}`);
      return true;
    }

    return false;
  }

  removeAllRouteAttempts(athleteId: string, routeNumber: number): number {
    console.log(
      `🗑️ Removing ALL attempts for athlete ${athleteId}, route ${routeNumber}`,
    );

    const initialLength = this.data.attempts.length;
    this.data.attempts = this.data.attempts.filter(
      (a) => !(a.athleteId === athleteId && a.routeNumber === routeNumber),
    );

    const removedCount = initialLength - this.data.attempts.length;

    if (removedCount > 0) {
      this.saveData();
      console.log(`✅ Successfully removed ${removedCount} attempts`);
    }

    return removedCount;
  }

  clearAllAttempts(): number {
    console.log("🗑️ Clearing ALL attempts for ALL athletes");

    const initialLength = this.data.attempts.length;
    this.data.attempts = [];

    if (initialLength > 0) {
      this.saveData();
      console.log(`✅ Successfully cleared ${initialLength} attempts from all athletes`);
    }

    return initialLength;
  }

  // Judges
  validateJudge(password: string): Judge | null {
    return this.data.judges.find((j) => j.password === password) || null;
  }

  // Scoring
  getAthleteScore(athleteId: string): AthleteScore {
    const attempts = this.getAthleteAttempts(athleteId);
    const routeResults: RouteResult[] = [];

    for (let i = 1; i <= 40; i++) {
      const routeAttempts = attempts.filter((a) => a.routeNumber === i);

      if (routeAttempts.length === 0) {
        routeResults.push({
          routeNumber: i,
          status: "not-attempted",
          points: 0,
          attempts: 0,
        });
        continue;
      }

      const completedAttempt = routeAttempts.find(
        (a) => a.result === "completed",
      );

      if (completedAttempt) {
        routeResults.push({
          routeNumber: i,
          status: completedAttempt.pointType as "flash" | "second+",
          points: completedAttempt.points,
          attempts: routeAttempts.length,
          completedAt: completedAttempt.timestamp,
        });
      } else {
        routeResults.push({
          routeNumber: i,
          status: "failed",
          points: 0,
          attempts: routeAttempts.length,
        });
      }
    }

    // Get top 6 scoring routes
    const scoringRoutes = routeResults
      .filter((r) => r.points > 0)
      .sort((a, b) => b.points - a.points)
      .slice(0, 6);

    const totalScore = scoringRoutes.reduce(
      (sum, route) => sum + route.points,
      0,
    );

    return {
      athleteId,
      totalScore,
      routeResults,
      topSixRoutes: scoringRoutes,
    };
  }

  // Get detailed statistics for an athlete
  getAthleteStatistics(athleteId: string) {
    const attempts = this.getAthleteAttempts(athleteId);

    // Count failed attempts (not just failed routes)
    const totalFailedAttempts = attempts.filter(
      (a) => a.result === "failed",
    ).length;
    const totalCompletedRoutes = attempts.filter(
      (a) => a.result === "completed",
    ).length;
    const flashCount = attempts.filter((a) => a.pointType === "flash").length;
    const secondPlusCount = attempts.filter(
      (a) => a.pointType === "second+",
    ).length;

    return {
      totalFailedAttempts,
      totalCompletedRoutes,
      flashCount,
      secondPlusCount,
      totalAttempts: attempts.length,
    };
  }

  // Ranking
  getRanking(
    gender: "Masculino" | "Feminino",
  ): { athlete: Athlete; score: AthleteScore }[] {
    const athletes = this.data.athletes.filter((a) => a.gender === gender);

    return athletes
      .map((athlete) => ({
        athlete,
        score: this.getAthleteScore(athlete.id),
      }))
      .sort((a, b) => b.score.totalScore - a.score.totalScore);
  }

  // Backup
  exportData(): string {
    return JSON.stringify(this.data, null, 2);
  }

  importData(jsonData: string): void {
    try {
      const importedData = JSON.parse(jsonData);
      this.data = { ...this.data, ...importedData };
      this.saveData();
    } catch (error) {
      throw new Error("Dados inválidos para importação");
    }
  }
}

export const dataStore = new DataStore();
